<template>
  <div>
    <!-- The action sits in the append slot rather than in the content: a button in the flow
         makes the content taller than its text, and the icon then centres on the pair. -->
    <v-alert v-if="loadError" type="error" dense text class="mt-4">
      {{ t('loadError') }}
      <template v-slot:append>
        <v-btn text small color="error" @click="$emit('retry')">{{ t('reload') }}</v-btn>
      </template>
    </v-alert>
    <v-row align="center" class="mt-4 mb-2">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="search"
          :placeholder="t('search')"
          :aria-label="t('search')"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-btn-toggle v-model="status" mandatory color="primary" dense>
          <v-btn value="all">{{ t('all') }}</v-btn>
          <v-btn value="active">{{ t('activeOnly') }}</v-btn>
          <v-btn value="inactive">{{ t('inactiveOnly') }}</v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn color="primary" outlined :aria-label="t('newField')" :disabled="busy" @click="openDialog()">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-data-table
      :headers="headers"
      :items="visibleFields"
      :loading="loading"
      :no-data-text="t('empty')"
      :loading-text="t('loading')"
      item-key="key"
      :items-per-page="-1"
      hide-default-footer
      mobile-breakpoint="768"
    >
      <template v-slot:item.key="{ item }">
        <code>{{ item.key }}</code>
      </template>
      <template v-slot:item.active="{ item }">
        <!-- The nonce rebuilds the switch when a toggle is refused: the bound value did not change,
             so Vuetify would keep the internal state its own click produced and stay visually off. -->
        <v-switch
          :key="item.key + '-' + refusals"
          :input-value="item.active"
          :aria-label="$t('documentSettings.toggleAriaLabel', { field: item.label, types: item.documentTypeLabel })"
          :disabled="busy"
          color="primary"
          hide-details
          class="mt-0 py-2"
          @change="toggle(item.key, $event)"
        />
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex justify-end">
          <v-btn
            icon
            :aria-label="$t('documentSettings.editAriaLabel', { field: item.label })"
            :disabled="busy"
            @click="openDialog(item.key)"
          >
            <v-icon color="primary">mdi-square-edit-outline</v-icon>
          </v-btn>
          <!-- Only a field that was never saved can be dropped: the api refuses the removal of a stored
               key (custom_field_removal_not_allowed), a stored one is deactivated instead. -->
          <v-btn
            v-if="item.isNew"
            icon
            :aria-label="$t('documentSettings.deleteAriaLabel', { field: item.label })"
            :disabled="busy"
            @click="remove(item.key)"
          >
            <v-icon color="error">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
    <document-custom-field-dialog
      v-if="dialog"
      v-model="dialog"
      :form-value="selectedField"
      :fields="draft"
      @validate="updateDraft"
    />
  </div>
</template>
<script>
  import { mapState } from 'vuex'
  import DocumentCustomFieldDialog from '@/components/dialogs/DocumentCustomFieldDialog.vue'
  import { toDocumentFieldPayload } from '@/helpers/documentCustomFields'
  import { EventBus } from '@/event-bus'

  export default {
    name: 'DocumentCustomFields',
    components: { DocumentCustomFieldDialog },
    props: {
      fields: { type: Array, default: null },
      busy: { type: Boolean, default: false },
      committed: { type: Number, default: 0 },
      // Keys the display configuration selects as columns. The api refuses to deactivate one of
      // them, and it travels in the same document, so the refusal can be raised here on the click
      // rather than discovered on a save that was doomed from the start.
      columns: { type: Array, default: () => [] },
      loading: { type: Boolean, default: false },
      loadError: { type: Boolean, default: false },
    },
    data() {
      return {
        draft: [],
        saved: '[]',
        search: '',
        status: 'all',
        refusals: 0,
        dialog: false,
        selectedField: null,
      }
    },
    computed: {
      ...mapState(['isOctipasAdmin']),
      dirty() {
        return JSON.stringify(this.draft) !== this.saved
      },
      headers() {
        return [
          { text: this.t('key'), value: 'key' },
          { text: this.t('label'), value: 'label' },
          { text: this.t('documentType'), value: 'documentTypeLabel' },
          { text: this.t('dataType'), value: 'dataTypeLabel' },
          { text: this.t('active'), value: 'active' },
          { text: '', value: 'actions', sortable: false, align: 'end' },
        ]
      },
      // Identities the api already stores, which is what separates a droppable draft from a field
      // the registry is not allowed to lose.
      savedIdentities() {
        return new Set(JSON.parse(this.saved).map((field) => field.key))
      },
      visibleFields() {
        const query = (this.search || '').trim().toLocaleLowerCase()
        return this.draft
          .map((field) => ({
            ...field,
            isNew: !this.savedIdentities.has(field.key),
            label: field.labels?.[this.$i18n.locale] || field.labels?.en || field.key,
            documentTypeLabel: field.document_types.length
              ? field.document_types.map((type) => this.t(`types.${type}`)).join(', ')
              : this.t('allTypes'),
            dataTypeLabel: this.t(`dataTypes.${field.data_type}`),
          }))
          .filter(
            (field) =>
              (this.status === 'all' || field.active === (this.status === 'active')) &&
              (!query ||
                field.key.toLocaleLowerCase().includes(query) ||
                field.label.toLocaleLowerCase().includes(query))
          )
      },
    },
    watch: {
      // The view shows which tab holds unsaved work, including the one that is not on screen.
      dirty: {
        immediate: true,
        handler(dirty) {
          this.$emit('dirty', dirty)
        },
      },
      // The view carries the payload into the single save.
      draft: {
        deep: true,
        immediate: true,
        handler(draft) {
          this.$emit('change', draft.map(toDocumentFieldPayload))
        },
      },
      // The write happened, so what was a draft is now what the api stores — savedIdentities follows,
      // and the delete action leaves the rows that have just been persisted.
      committed() {
        this.saved = JSON.stringify(this.draft)
      },
      // The view owns the call, and hands the document down again after any save. Rebuilding the
      // baseline alongside the draft is what keeps `dirty` false right after a load, so Save stays
      // disabled until the admin actually changes something.
      // A reload must never cost them work in progress, so a dirty draft stands — stale baseline
      // included. Saving from there fails with custom_field_removal_not_allowed, which the screen
      // already turns into "the registry changed, reload", rather than silently dropping the edits.
      fields: {
        immediate: true,
        handler(fields) {
          if (this.dirty) {
            return
          }
          this.draft = (fields || []).map(toDocumentFieldPayload)
          this.saved = JSON.stringify(this.draft)
        },
      },
    },
    methods: {
      t(key) {
        return this.$t(`documentSettings.${key}`)
      },
      openDialog(identity = null) {
        this.selectedField = this.draft.find((field) => field.key === identity) || null
        this.dialog = true
      },
      updateDraft(field) {
        if (this.selectedField) {
          const index = this.draft.findIndex((item) => item.key === this.selectedField.key)
          this.$set(this.draft, index, field)
        } else {
          this.draft.push(field)
        }
      },
      // Drops a field that was never saved. A stored one has no delete: the api refuses to lose a key
      // it already carries, and deactivating is the way to retire it.
      remove(identity) {
        if (this.busy || !this.isOctipasAdmin || this.savedIdentities.has(identity)) {
          return
        }
        const index = this.draft.findIndex((field) => field.key === identity)
        if (index === -1) {
          return
        }
        this.draft.splice(index, 1)
      },
      // The status is a draft like every other edit of this screen: one control must not write to the
      // api on a stored field and stay local on a field that is not saved yet, since nothing tells the
      // two rows apart. Save persists the whole registry anyway, so writing here buys no granularity.
      toggle(identity, active) {
        if (this.busy || !this.isOctipasAdmin) {
          return
        }
        const field = this.draft.find((item) => item.key === identity)
        if (!field) {
          return
        }
        // Refused on the spot, and the switch stays where it was: letting it move would promise
        // something Save cannot deliver. The columns we hold may be behind another admin's edit, so
        // this only spares the doomed round trip — the api stays the authority, and save() still
        // reports custom_field_in_use when our copy was out of date.
        if (!active && this.columns.includes(identity)) {
          this.refusals++
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('documentSettings.fieldInUse', { fields: identity }),
            color: 'error',
          })
          return
        }
        field.active = active
      },
    },
  }
</script>
